import React, { useMemo, useState } from 'react';
import { View, Image, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import type { ICommunityResourceFile } from '@/types/shared/resources';
import { getFileKind } from '@/utils/common/file';
import { openExternalUrl } from '@/utils/common/linking';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface FileRowProps {
  file: ICommunityResourceFile;
  onOpenImage: (file: ICommunityResourceFile) => void;
  onOpenPdf: (file: ICommunityResourceFile) => void;
}

export const FileRow: React.FC<FileRowProps> = ({ file, onOpenImage, onOpenPdf }) => {
  const { semantic } = useThemeColors();
  const kind = useMemo(() => getFileKind(file), [file]);
  const [thumbFailed, setThumbFailed] = useState(false);
  const label = file.label || `File ${file.id}`;

  const handlePress = () => {
    if (kind === 'image') return onOpenImage(file);
    if (kind === 'pdf') return onOpenPdf(file);
    openExternalUrl(file.fileUrl);
  };

  const leading = (() => {
    if (kind === 'image' && !thumbFailed) {
      return (
        <Image
          testID="file-row-thumbnail"
          source={{ uri: file.fileUrl }}
          style={[styles.thumbnail, { backgroundColor: semantic.background.tertiary }]}
          onError={() => setThumbFailed(true)}
        />
      );
    }
    const iconName =
      kind === 'pdf' ? 'document-text-outline'
        : kind === 'image' ? 'image-outline'
        : 'document-outline';
    return (
      <View style={[styles.iconWrapper, { backgroundColor: semantic.background.secondary }]}>
        <Icon name={iconName} size={24} color={semantic.text.primary} />
      </View>
    );
  })();

  return (
    <TouchableOpacity testID="file-row-touchable" style={styles.row} onPress={handlePress}>
      {leading}
      <Text numberOfLines={1} style={[styles.label, { color: semantic.text.primary }]}>{label}</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 12,
  },
  thumbnail: {
    width: 60,
    height: 60,
    borderRadius: 8,
    // backgroundColor applied inline from semantic.background.tertiary — a
    // hardcoded #eee flashed as a bright tile on the dark surface while
    // loading, and stayed white if the image 404s.
  },
  iconWrapper: {
    width: 60,
    height: 60,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    flex: 1,
    fontSize: 15,
  },
});
